import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { processesApi, subProcessesApi, operationsApi } from '../../services/apiService';
import { SubProcess, Operation } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;
type RouteProps = RouteProp<MainStackParamList, 'AddOperation'>;

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// ============================================================
// Portal-based Dropdown (fixed positioning) — same approach as
// AddProcessScreen so the menu escapes overflow/stacking contexts.
// ============================================================
interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<View>;
  children: React.ReactNode;
  backgroundColor: string;
  borderColor: string;
}

const PortalDropdown = ({ isOpen, onClose, buttonRef, children, backgroundColor, borderColor }: DropdownProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, availableHeight: 300 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      (buttonRef.current as any).measureInWindow((x: number, y: number, width: number, height: number) => {
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - (y + height + 4);
        const spaceAbove = y - 4;
        const maxDropdown = 300;
        const openUpward = spaceBelow < 150 && spaceAbove > spaceBelow;
        setPosition({
          top: openUpward ? Math.max(4, y - Math.min(maxDropdown, spaceAbove)) : y + height + 4,
          left: x,
          width,
          availableHeight: openUpward ? Math.min(maxDropdown, spaceAbove) : Math.min(maxDropdown, spaceBelow),
        });
      });
    }
  }, [isOpen, buttonRef]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dropdownMenu = (
    <>
      {/* eslint-disable-next-line */}
      <div
        onMouseDown={(e: any) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        style={{ position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
      />
      <div
        ref={dropdownRef}
        onMouseDown={(e: any) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: position.width,
          backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          maxHeight: position.availableHeight,
          overflowY: 'auto' as const,
        }}
      >
        {children}
      </div>
    </>
  );

  return ReactDOM.createPortal(dropdownMenu, document.body);
};

// Mirror of the server's join_code_segments: drop empty parts, join with '/'.
const joinCodeSegments = (...codes: (string | null | undefined)[]): string => {
  return codes
    .map(c => (c == null ? '' : String(c).trim()))
    .filter(c => c.length > 0)
    .join('/');
};

// Suggest the next code = (max numeric existing code among siblings) + 1.
// Non-numeric / empty codes are ignored. Returns '1' for an empty set.
const nextCode = (rows: { code?: string | null }[]): string => {
  const max = rows.reduce((m, r) => {
    const n = parseInt((r.code ?? '').trim(), 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return String(max + 1);
};

export default function AddOperationScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const processId = route.params?.processId;
  const presetSubProcessId = route.params?.subProcessId;
  const operationId = route.params?.operationId;

  const { isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { width } = useWindowDimensions();
  const { showSuccess, showError } = useNotification();

  const isEditMode = !!operationId;
  const isSmallScreen = width < 768;

  // Same gate as the sub-process screen: proposing/editing operational items
  // is governed by `hrcpms.executive_items.create` (not coarse isViewOnly).
  const canCreateOperationalItem = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );

  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: isDark ? '#6B7280' : '#9CA3AF',
    dropdownTextSelected: colors.textPrimary,
    dropdownMenu: colors.card,
    dropdownMenuBorder: colors.border,
    dropdownItem: colors.textPrimary,
    dropdownItemBorder: isDark ? colors.border : '#F3F4F6',
    readonlyField: isDark ? colors.backgroundAlt : '#EEF2F7',
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    loadingText: colors.textMuted,
    disabledField: isDark ? '#1F2937' : '#F1F5F9',
  };

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mainProcessName, setMainProcessName] = useState('');
  const [subProcesses, setSubProcesses] = useState<SubProcess[]>([]);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [showSubProcessDropdown, setShowSubProcessDropdown] = useState(false);
  const [showOperationDropdown, setShowOperationDropdown] = useState(false);
  const subProcessButtonRef = useRef<View>(null);
  const operationButtonRef = useRef<View>(null);

  // Level 1 (sub-process) and Level 2 (operation) each resolve to EITHER an
  // existing entry ('existing') OR a freshly-created one ('new').
  const [subMode, setSubMode] = useState<'existing' | 'new'>('existing');
  // 'pick' = dropdown shown, user hasn't chosen yet; 'existing' = picked an
  // existing operation; 'new' = adding a new one (inline inputs, dropdown hidden).
  const [opMode, setOpMode] = useState<'pick' | 'existing' | 'new'>('pick');
  const [selectedOperationId, setSelectedOperationId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    subProcessId: presetSubProcessId || (null as number | null),
    newSubName: '',   // when subMode === 'new'
    newSubCode: '',   // when subMode === 'new'
    name: '',         // operation name (new / edit)
    code: '',         // operation code (new / edit)
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useUnsavedChangesWarning(
    !!formData.name || !!formData.code || !!formData.description ||
    !!formData.newSubName || !!formData.newSubCode
  );

  const selectedSubProcess = subProcesses.find(sp => sp.id === formData.subProcessId) || null;
  const selectedOperation = operations.find(o => o.id === selectedOperationId) || null;

  // Derive the main-process code prefix from an existing sibling sub-process's
  // full_code (strip its last segment). Lets us preview a NEW sub-process's
  // path before it's saved; the real value is computed server-side.
  const siblingPrefix = (): string | null => {
    const sib = subProcesses.find(sp => !!sp.full_code);
    if (!sib || !sib.full_code) return null;
    const parts = sib.full_code.split('/');
    parts.pop();
    return parts.join('/') || null;
  };

  const subFullCode = subMode === 'new'
    ? (joinCodeSegments(siblingPrefix(), formData.newSubCode) || null)
    : (selectedSubProcess?.full_code || null);

  // For an existing operation the path is already known; for a new one we
  // preview sub_full_code + '/' + code.
  const previewFullCode = (opMode === 'existing' && !isEditMode)
    ? (selectedOperation?.full_code || '')
    : joinCodeSegments(subFullCode, formData.code);

  // Whether each level resolves to something saveable (gates the submit button).
  const subResolved = subMode === 'existing'
    ? !!formData.subProcessId
    : (!!formData.newSubName.trim() && !!formData.newSubCode.trim());
  const opResolved = isEditMode
    ? (!!formData.name.trim() && !!formData.code.trim())
    : opMode === 'existing'
      ? !!selectedOperationId
      : opMode === 'new'
        ? (!!formData.name.trim() && !!formData.code.trim())
        : false; // 'pick' — nothing chosen yet
  const subProcessSelected = subResolved;

  // Redirect view-only users who lack the create permission.
  useEffect(() => {
    if (isViewOnly && !canCreateOperationalItem) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigation.navigate('Processes' as any);
    }
  }, [isViewOnly, canCreateOperationalItem, navigation]);

  useEffect(() => {
    fetchData();
  }, [processId, operationId]);

  const loadOperations = async (subId: number) => {
    try {
      const data = await operationsApi.list({ sub_process: subId, page_size: 200 });
      setOperations(data.results || []);
    } catch {
      setOperations([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Dropdown 1 source: sub-processes under the inherited main process.
      // When no main process is inherited, fall back to all visible ones.
      const subParams: Record<string, any> = { page_size: 200 };
      if (processId) subParams.process = processId;
      const subData = await subProcessesApi.list(subParams);
      setSubProcesses(subData.results || []);

      if (processId) {
        try {
          const proc = await processesApi.get(processId);
          setMainProcessName(proc.name || '');
        } catch {
          // Name is cosmetic; ignore failure.
        }
      }

      if (operationId) {
        // Edit mode — load the operation and pin its parent sub-process.
        const op = await operationsApi.get(operationId);
        setSubMode('existing');
        setOpMode('new');
        setFormData(prev => ({
          ...prev,
          subProcessId: op.sub_process,
          name: op.name || '',
          code: op.code || '',
          description: op.description || '',
        }));
        if (op.sub_process && !(subData.results || []).some(sp => sp.id === op.sub_process)) {
          try {
            const sp = await subProcessesApi.get(op.sub_process);
            setSubProcesses(prev => [sp, ...prev]);
          } catch {
            // Non-fatal — preview just won't show the inherited prefix.
          }
        }
        if (op.sub_process) await loadOperations(op.sub_process);
      } else if (presetSubProcessId) {
        // Preset parent (navigated from a sub-process) — load its children.
        setSubMode('existing');
        await loadOperations(presetSubProcessId);
      }
    } catch (err) {
      showError(t('processes.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ---- Level 1 (sub-process) handlers ----
  const handleSelectSubProcess = (id: number) => {
    // Changing the parent clears the operation level (new context).
    setSubMode('existing');
    setFormData(prev => ({ ...prev, subProcessId: id, newSubName: '', newSubCode: '', name: '', code: '' }));
    setSelectedOperationId(null);
    setOpMode('pick'); // show the operation dropdown for this sub-process
    setOperations([]);
    setErrors({});
    setShowSubProcessDropdown(false);
    loadOperations(id);
  };

  const handleAddNewSubProcess = () => {
    setSubMode('new');
    setFormData(prev => ({
      ...prev,
      subProcessId: null,
      newSubName: '',
      newSubCode: nextCode(subProcesses),
      name: '',
      code: '',
    }));
    setSelectedOperationId(null);
    setOpMode('new'); // a brand-new sub-process has no existing children yet
    setOperations([]);
    setErrors({});
    setShowSubProcessDropdown(false);
  };

  // ---- Level 2 (operation) handlers ----
  const handleSelectOperation = (op: Operation) => {
    setOpMode('existing');
    setSelectedOperationId(op.id);
    setFormData(prev => ({ ...prev, name: '', code: '' }));
    setErrors({});
    setShowOperationDropdown(false);
  };

  const handleAddNewOperation = () => {
    setOpMode('new');
    setSelectedOperationId(null);
    setFormData(prev => ({ ...prev, name: '', code: nextCode(operations) }));
    setErrors({});
    setShowOperationDropdown(false);
  };

  // Switch back from "add new operation" to the existing-operations dropdown.
  const handlePickExistingInstead = () => {
    setOpMode('pick');
    setSelectedOperationId(null);
    setFormData(prev => ({ ...prev, name: '', code: '' }));
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Level 1
    if (subMode === 'existing') {
      if (!formData.subProcessId) newErrors.subProcess = t('operations.subProcessRequired');
    } else {
      if (!processId) newErrors.subProcess = t('operations.mainProcessRequiredForNew');
      if (!formData.newSubName.trim()) newErrors.newSubName = t('operations.subProcessNameRequired');
      const c = formData.newSubCode.trim();
      if (!c) newErrors.newSubCode = t('operations.subProcessCodeRequired');
      else if (!/^\d+$/.test(c)) newErrors.newSubCode = t('operations.codeMustBeNumeric');
      else if (subProcesses.some(sp => (sp.code ?? '').trim() === c)) newErrors.newSubCode = t('operations.codeDuplicate');
    }

    // Level 2
    if (!isEditMode && opMode === 'pick') {
      // Hasn't chosen "add new" or an existing operation yet.
      newErrors.operation = t('operations.operationRequired');
    } else if (!isEditMode && opMode === 'existing') {
      if (!selectedOperationId) newErrors.operation = t('operations.operationRequired');
    } else {
      // 'new' (or edit mode) — validate the typed name + code
      if (!formData.name.trim()) newErrors.name = t('operations.operationNameRequired');
      const c = formData.code.trim();
      if (!c) newErrors.code = t('operations.operationCodeRequired');
      else if (!/^\d+$/.test(c)) newErrors.code = t('operations.codeMustBeNumeric');
      else if (
        // Duplicate check only meaningful within an existing sub-process's
        // children (a new sub-process starts empty). Exclude self when editing.
        subMode === 'existing'
        && operations.some(o => (o.code ?? '').trim() === c && o.id !== operationId)
      ) newErrors.code = t('operations.codeDuplicate');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }
    setSubmitting(true);
    try {
      // 1. Resolve the sub-process (create it first if new).
      let subId = formData.subProcessId;
      if (subMode === 'new') {
        try {
          const createdSub = await subProcessesApi.create({
            process: processId,
            name: formData.newSubName.trim(),
            code: formData.newSubCode.trim(),
          } as Partial<SubProcess>);
          subId = createdSub.id;
        } catch (err: any) {
          const data = err?.response?.data;
          let message = t('operations.subProcessSaveFailed');
          if (data?.code) message = Array.isArray(data.code) ? data.code[0] : data.code;
          else if (data?.name) message = Array.isArray(data.name) ? data.name[0] : data.name;
          else if (data?.detail) message = data.detail;
          showError(message);
          setSubmitting(false);
          return;
        }
      }

      // 2. Resolve the operation.
      if (isEditMode && operationId) {
        await operationsApi.update(operationId, {
          sub_process: subId!,
          name: formData.name.trim(),
          code: formData.code.trim(),
          description: formData.description.trim(),
        });
        showSuccess(t('operations.updateSuccess'));
      } else if (opMode === 'existing') {
        // Already a saved entry — both levels resolved, nothing to create.
        showSuccess(t('operations.saveSuccess'));
      } else {
        await operationsApi.create({
          sub_process: subId!,
          name: formData.name.trim(),
          code: formData.code.trim(),
          description: formData.description.trim(),
        });
        showSuccess(t('operations.saveSuccess'));
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
      if (navigation.canGoBack()) navigation.goBack();
      else navigation.navigate('Processes' as any);
    } catch (err: any) {
      const data = err?.response?.data;
      let message = isEditMode ? t('operations.updateFailed') : t('operations.saveFailed');
      if (data?.code) message = Array.isArray(data.code) ? data.code[0] : data.code;
      else if (data?.detail) message = data.detail;
      else if (data?.non_field_errors) message = data.non_field_errors[0];
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('Processes' as any);
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  // Label shown on the sub-process dropdown button.
  const subButtonLabel = subMode === 'new'
    ? (formData.newSubName.trim() || t('operations.addNewSubProcess'))
    : (selectedSubProcess
        ? (selectedSubProcess.full_code ? `${selectedSubProcess.name}  (${selectedSubProcess.full_code})` : selectedSubProcess.name)
        : t('operations.selectOrAddSubProcess'));
  const subButtonActive = subMode === 'new' || !!selectedSubProcess;

  // Label shown on the operation dropdown button.
  // Dropdown is hidden while opMode === 'new', so the label only covers the
  // pick / existing states.
  const opButtonLabel = selectedOperation
    ? (selectedOperation.full_code ? `${selectedOperation.name}  (${selectedOperation.full_code})` : selectedOperation.name)
    : t('operations.selectOperation');
  const opButtonActive = !!selectedOperation;

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.processes'), route: 'Processes' },
              { label: isEditMode ? t('operations.editOperation') : t('operations.addOperation') },
            ]}
          />

          <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
            <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
              {isEditMode ? t('operations.editOperation') : t('operations.addOperation')}
            </Text>

            <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: (showSubProcessDropdown || showOperationDropdown) ? 9999 : 20, overflow: 'visible' }]}>
              <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

              {/* Inherited main process (read-only context) */}
              {!!mainProcessName && (
                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('operations.mainProcess')}</Text>
                  <View style={[styles.readonlyBox, { backgroundColor: themeColors.readonlyField, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.readonlyText, { color: themeColors.inputText }]}>{mainProcessName}</Text>
                  </View>
                </View>
              )}

              {/* ============ FIELD 1 — العملية الفرعية (creatable) ============ */}
              <View style={[styles.formGroup, { zIndex: showSubProcessDropdown ? 9999 : 10, overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('operations.subProcess')} <Text style={styles.required}>*</Text>
                </Text>
                {/* In edit mode the parent sub-process is fixed (read-only). */}
                {isEditMode ? (
                  <View style={[styles.readonlyBox, { backgroundColor: themeColors.readonlyField, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.readonlyText, { color: themeColors.inputText }]}>
                      {selectedSubProcess ? selectedSubProcess.name : '—'}
                    </Text>
                  </View>
                ) : (
                  <View style={styles.dropdownWrapper}>
                    <TouchableOpacity
                      ref={subProcessButtonRef as any}
                      style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }, errors.subProcess ? styles.inputError : undefined]}
                      onPress={() => setShowSubProcessDropdown(v => !v)}
                    >
                      <ChevronDownIcon size={16} color={themeColors.iconColor} />
                      <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, subButtonActive ? { color: themeColors.dropdownTextSelected } : undefined]}>
                        {subButtonLabel}
                      </Text>
                    </TouchableOpacity>
                    <PortalDropdown
                      isOpen={showSubProcessDropdown}
                      onClose={() => setShowSubProcessDropdown(false)}
                      buttonRef={subProcessButtonRef}
                      backgroundColor={themeColors.dropdownMenu}
                      borderColor={themeColors.dropdownMenuBorder}
                    >
                      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
                        {subProcesses.map(sp => (
                          <TouchableOpacity
                            key={sp.id}
                            style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                            onPress={() => handleSelectSubProcess(sp.id)}
                          >
                            <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>
                              {sp.full_code ? `${sp.name}  (${sp.full_code})` : sp.name}
                            </Text>
                          </TouchableOpacity>
                        ))}
                        {/* Last option — create a new sub-process */}
                        <TouchableOpacity
                          style={[styles.dropdownItem, styles.addNewItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                          onPress={handleAddNewSubProcess}
                        >
                          <Text style={[styles.dropdownItemText, styles.addNewText]}>
                            {t('operations.addNewSubProcess')}
                          </Text>
                        </TouchableOpacity>
                      </ScrollView>
                    </PortalDropdown>
                  </View>
                )}
                {errors.subProcess && <Text style={styles.errorText}>{errors.subProcess}</Text>}
              </View>

              {/* Inline inputs for a NEW sub-process */}
              {!isEditMode && subMode === 'new' && (
                <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, styles.inlineNewBox]}>
                  <View style={[styles.formGroup, styles.formGroupHalf, { marginBottom: 0 }]}>
                    <Text style={[styles.label, { color: themeColors.label }]}>
                      {t('operations.newSubProcessName')} <Text style={styles.required}>*</Text>
                    </Text>
                    <TextInput
                      style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.newSubName ? styles.inputError : undefined]}
                      value={formData.newSubName}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, newSubName: text }))}
                      placeholder={t('operations.newSubProcessName')}
                      placeholderTextColor={themeColors.placeholder}
                      textAlign="right"
                    />
                    {errors.newSubName && <Text style={styles.errorText}>{errors.newSubName}</Text>}
                  </View>
                  <View style={[styles.formGroup, styles.formGroupHalf, { marginBottom: 0 }]}>
                    <Text style={[styles.label, { color: themeColors.label }]}>
                      {t('operations.codeLabel')} <Text style={styles.required}>*</Text>
                    </Text>
                    <TextInput
                      style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.newSubCode ? styles.inputError : undefined]}
                      value={formData.newSubCode}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, newSubCode: text }))}
                      placeholder={t('operations.operationCodeHint')}
                      placeholderTextColor={themeColors.placeholder}
                      keyboardType="numeric"
                      textAlign="right"
                    />
                    {errors.newSubCode && <Text style={styles.errorText}>{errors.newSubCode}</Text>}
                  </View>
                </View>
              )}

              {/* ============ FIELD 2 — اسم العملية (creatable, dependent) ============ */}
              <View style={[styles.formGroup, { zIndex: showOperationDropdown ? 9998 : 5, overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('operations.operation')} <Text style={styles.required}>*</Text>
                </Text>
                {/* Edit mode: operation name is a plain editable input */}
                {isEditMode ? (
                  <TextInput
                    style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                    value={formData.name}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, name: text }))}
                    placeholder={t('operations.operationName')}
                    placeholderTextColor={themeColors.placeholder}
                    textAlign="right"
                  />
                ) : opMode === 'new' ? null : (
                  <View style={styles.dropdownWrapper}>
                    <TouchableOpacity
                      ref={operationButtonRef as any}
                      style={[
                        styles.dropdown,
                        { backgroundColor: subProcessSelected ? themeColors.dropdown : themeColors.disabledField, borderColor: themeColors.dropdownBorder },
                        errors.operation ? styles.inputError : undefined,
                      ]}
                      disabled={!subProcessSelected}
                      onPress={() => setShowOperationDropdown(v => !v)}
                    >
                      <ChevronDownIcon size={16} color={themeColors.iconColor} />
                      <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, (subProcessSelected && opButtonActive) ? { color: themeColors.dropdownTextSelected } : undefined]}>
                        {subProcessSelected ? opButtonLabel : t('operations.selectSubProcessFirst')}
                      </Text>
                    </TouchableOpacity>
                    <PortalDropdown
                      isOpen={showOperationDropdown}
                      onClose={() => setShowOperationDropdown(false)}
                      buttonRef={operationButtonRef}
                      backgroundColor={themeColors.dropdownMenu}
                      borderColor={themeColors.dropdownMenuBorder}
                    >
                      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
                        {operations.map(op => (
                          <TouchableOpacity
                            key={op.id}
                            style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                            onPress={() => handleSelectOperation(op)}
                          >
                            <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>
                              {op.full_code ? `${op.name}  (${op.full_code})` : op.name}
                            </Text>
                          </TouchableOpacity>
                        ))}
                        {/* Last option — create a new operation */}
                        <TouchableOpacity
                          style={[styles.dropdownItem, styles.addNewItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                          onPress={handleAddNewOperation}
                        >
                          <Text style={[styles.dropdownItemText, styles.addNewText]}>
                            {t('operations.addNewOperation')}
                          </Text>
                        </TouchableOpacity>
                      </ScrollView>
                    </PortalDropdown>
                  </View>
                )}
                {errors.operation && <Text style={styles.errorText}>{errors.operation}</Text>}
              </View>

              {/* Inline inputs for a NEW operation. Shown only when opMode === 'new'
                  (the Field 2 dropdown is hidden in that state) or when editing,
                  so the user never sees a dropdown AND inputs at the same time. */}
              {(opMode === 'new' || isEditMode) && (
                <>
                  {!isEditMode && operations.length > 0 && (
                    <TouchableOpacity onPress={handlePickExistingInstead} style={styles.pickExistingLink}>
                      <Text style={styles.addNewText}>{t('operations.pickExistingOperation')}</Text>
                    </TouchableOpacity>
                  )}
                  <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, !isEditMode && styles.inlineNewBox]}>
                  {!isEditMode && (
                    <View style={[styles.formGroup, styles.formGroupHalf, { marginBottom: 0 }]}>
                      <Text style={[styles.label, { color: themeColors.label }]}>
                        {t('operations.operationName')} <Text style={styles.required}>*</Text>
                      </Text>
                      <TextInput
                        style={[
                          styles.input,
                          { backgroundColor: subProcessSelected ? themeColors.input : themeColors.disabledField, borderColor: themeColors.inputBorder, color: themeColors.inputText },
                          errors.name ? styles.inputError : undefined,
                        ]}
                        value={formData.name}
                        editable={subProcessSelected}
                        onChangeText={(text) => setFormData(prev => ({ ...prev, name: text }))}
                        placeholder={subProcessSelected ? t('operations.operationName') : t('operations.selectSubProcessFirst')}
                        placeholderTextColor={themeColors.placeholder}
                        textAlign="right"
                      />
                      {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
                    </View>
                  )}
                  <View style={[styles.formGroup, styles.formGroupHalf, { marginBottom: 0 }]}>
                    <Text style={[styles.label, { color: themeColors.label }]}>
                      {t('operations.operationCode')} <Text style={styles.required}>*</Text>
                    </Text>
                    <TextInput
                      style={[
                        styles.input,
                        { backgroundColor: subProcessSelected ? themeColors.input : themeColors.disabledField, borderColor: themeColors.inputBorder, color: themeColors.inputText },
                        errors.code ? styles.inputError : undefined,
                      ]}
                      value={formData.code}
                      editable={subProcessSelected}
                      onChangeText={(text) => setFormData(prev => ({ ...prev, code: text }))}
                      placeholder={subProcessSelected ? t('operations.operationCodeHint') : t('operations.selectSubProcessFirst')}
                      placeholderTextColor={themeColors.placeholder}
                      keyboardType="numeric"
                      textAlign="right"
                    />
                    {errors.code && <Text style={styles.errorText}>{errors.code}</Text>}
                  </View>
                  </View>
                </>
              )}

              {/* Description (optional) */}
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('processes.processDescription')}</Text>
                <TextInput
                  style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.description}
                  onChangeText={(text) => setFormData(prev => ({ ...prev, description: text }))}
                  multiline
                  numberOfLines={3}
                  textAlign="right"
                  textAlignVertical="top"
                />
              </View>

              {/* Read-only full code preview — built server-side on save */}
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('operations.fullCode')}</Text>
                <View style={[styles.readonlyBox, { backgroundColor: themeColors.readonlyField, borderColor: themeColors.inputBorder }]}>
                  <Text style={[styles.codeText, { color: previewFullCode ? themeColors.inputText : themeColors.placeholder }]}>
                    {previewFullCode || t('operations.fullCodeEmpty')}
                  </Text>
                </View>
                {subProcessSelected && opMode === 'new' && !subFullCode && (
                  <Text style={[styles.hintText, { color: themeColors.placeholder }]}>{t('operations.missingAncestorCodes')}</Text>
                )}
              </View>
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <TouchableOpacity style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]} onPress={handleCancel}>
                <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveButton, (submitting || !subResolved || !opResolved) && styles.saveButtonDisabled]}
                onPress={handleSave}
                disabled={submitting || !subResolved || !opResolved}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.saveButtonText}>{isEditMode ? t('common.update') : t('common.save')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  container: { flex: 1, padding: 24, paddingTop: 16, overflow: 'visible' },
  containerSmall: { padding: 16 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 12, fontSize: 14, fontFamily: FONTS.regular, color: COLORS.neutral600 },
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  pageTitle: { fontSize: 24, fontFamily: FONTS.medium, color: COLORS.neutral900, textAlign: 'right', marginBottom: 24 },
  section: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    padding: 24,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'visible',
  },
  sectionTitle: { fontSize: 16, fontFamily: FONTS.medium, color: COLORS.neutral900, textAlign: 'right', marginBottom: 20 },
  formRow: { flexDirection: 'row-reverse', gap: 16, marginBottom: 16, overflow: 'visible' },
  formRowSmall: { flexDirection: 'column' },
  formGroup: { marginBottom: 16, overflow: 'visible' },
  formGroupHalf: { flex: 1, overflow: 'visible' },
  inlineNewBox: {
    backgroundColor: 'rgba(169, 33, 31, 0.04)',
    borderWidth: 1,
    borderColor: 'rgba(169, 33, 31, 0.25)',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  label: { fontSize: 14, fontFamily: FONTS.medium, color: COLORS.neutral700, textAlign: 'right', marginBottom: 8 },
  required: { color: COLORS.primary },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  inputError: { borderColor: '#EF4444' },
  textarea: { minHeight: 60, textAlignVertical: 'top' },
  errorText: { fontSize: 12, fontFamily: FONTS.regular, color: '#EF4444', textAlign: 'right', marginTop: 4 },
  hintText: { fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right', marginTop: 4 },
  dropdownWrapper: { position: 'relative', zIndex: 10, overflow: 'visible' },
  dropdown: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownText: { fontSize: 14, fontFamily: FONTS.regular, color: '#9CA3AF', flex: 1, textAlign: 'right', writingDirection: 'rtl' },
  dropdownItem: { paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  dropdownItemText: { fontSize: 14, fontFamily: FONTS.regular, color: COLORS.neutral700, textAlign: 'right', writingDirection: 'rtl' },
  addNewItem: { backgroundColor: 'rgba(169, 33, 31, 0.06)' },
  addNewText: { color: COLORS.primary, fontFamily: FONTS.medium, textAlign: 'right' },
  pickExistingLink: { marginBottom: 8, alignItems: 'flex-start' },
  readonlyBox: {
    backgroundColor: '#EEF2F7',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 36,
    justifyContent: 'center',
  },
  readonlyText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042', textAlign: 'right' },
  codeText: { fontSize: 15, fontFamily: FONTS.medium, textAlign: 'right', writingDirection: 'ltr', letterSpacing: 1 },
  footer: { flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 24 },
  saveButton: { backgroundColor: COLORS.primary, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 8, minWidth: 80, alignItems: 'center' },
  saveButtonDisabled: { opacity: 0.6 },
  saveButtonText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
  cancelButton: { backgroundColor: '#FFFFFF', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E5E7EB' },
  cancelButtonText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042' },
});
